import type { Router, RouteLocationRaw } from 'vue-router';
import type { Store } from 'vuex';
import { currentJwt } from '@/lib/jwt';
import { writeDomainCookie } from '@/lib/domain_cookie';
import { identifyAccountInSegmentOnce, resetIdentifiedAccount } from '@/lib/identifyAccountInSegmentOnce';
import { syncGrowthbookUserAttributes } from '@/lib/growthbook';
import type { Account } from '@/models/account';

export type AuthSessionTokens = {
  jwt: string;
  refresh: string;
};

export type EstablishAuthenticatedSessionOptions = {
  store: Store<unknown>;
  /** When provided, persists login cookies before loading account. */
  tokens?: AuthSessionTokens;
  /** Feature-specific Segment tracks after identify. Skipped when account is unavailable. */
  tracks?: (account: Account) => void | Promise<void>;
  /** Optional post-session navigation. Prefer router.push for in-app paths. */
  navigateTo?: RouteLocationRaw;
  router?: Router;
};

function isAbsoluteUrl(target: string): boolean {
  return /^https?:\/\//i.test(target) || target.startsWith('//');
}

/**
 * Auth-handoff helper for signup/login success and feature entry that must attach Segment identity
 * before tracks or SPA navigation:
 * persist tokens (optional) → load account → Segment identify once → sync GrowthBook userId →
 * optional tracks → optional navigate.
 *
 * Do not call from global route bootstrap (`ensureAccountLoadedOnce`). Navigation should only await
 * account fetch; identify belongs at explicit auth transitions.
 *
 * When tokens are provided, always refetch account and reset Segment identify dedupe so a prior
 * persisted Vuex account (e.g. on skipAuth signup routes) cannot drive analytics for the wrong user.
 */
export async function establishAuthenticatedSession(options: EstablishAuthenticatedSessionOptions): Promise<Account | null> {
  const { store, tokens, tracks, navigateTo, router } = options;
  const wroteTokens = Boolean(tokens?.jwt && tokens?.refresh);

  if (tokens?.jwt && tokens?.refresh) {
    writeDomainCookie('jwt', tokens.jwt);
    writeDomainCookie('refresh', tokens.refresh);
    resetIdentifiedAccount();
  }

  const shouldFetchAccount = Boolean(currentJwt()) && (wroteTokens || !store.getters['account/getAccount']);

  if (shouldFetchAccount) {
    try {
      await store.dispatch('account/fetchAccountData');
    } catch (error) {
      console.error('Failed to fetch account data while establishing authenticated session:', error);
    }
  }

  const account = store.getters['account/getAccount'] as Account | null;

  if (!account) {
    return null;
  }

  await identifyAccountInSegmentOnce(account);
  // GrowthBook may have been initialized while logged out; refresh userId before SPA navigate + isOn().
  await syncGrowthbookUserAttributes();

  if (tracks) {
    await tracks(account);
  }

  if (navigateTo !== undefined) {
    await navigateAfterAuth(navigateTo, router);
  }

  return account;
}

async function navigateAfterAuth(navigateTo: RouteLocationRaw, router?: Router): Promise<void> {
  if (typeof navigateTo === 'string' && isAbsoluteUrl(navigateTo)) {
    window.location.href = navigateTo;
    return;
  }

  if (router) {
    await router.push(navigateTo);
    return;
  }

  if (typeof navigateTo === 'string') {
    window.location.href = navigateTo;
  }
}
